import React, { useCallback, type ComponentPropsWithRef, type RefAttributes } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponentClass } from 'styled-components';
import { useFragment, graphql } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import EditorHorizontalRuleIcon from '@atlaskit/icon/glyph/editor/horizontal-rule';
import { BitbucketIcon, JiraIcon } from '@atlaskit/logo';
import { Text } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import ConnectingWaves from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/connecting-waves';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { main_softwareCodePage_OneClickConnect$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_OneClickConnect.graphql';
import { useOneClickConnectStore } from '../../../../controllers';
import {
	IS_CONNECTED,
	IS_CONNECTING,
	READY_TO_CONNECT,
} from '../../../../controllers/one-click-connect-store/types';
import connectingImg from './assets/connecting.svg';
import messages from './messages';

type OneClickConnectProps = {
	oneClickConnectData: main_softwareCodePage_OneClickConnect$key;
	onExitOnClickConnectFlow: () => void;
	onRefetch: () => void;
};

export const OneClickConnect = ({
	oneClickConnectData,
	onExitOnClickConnectFlow,
	onRefetch,
}: OneClickConnectProps) => {
	const { formatMessage } = useIntl();
	const baseUrl = window.location.origin;
	const tenantHostName = window.location.host;
	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_OneClickConnect on Query {
				codeInJira(cloudId: $cloudId) @required(action: THROW) {
					userConfiguration @required(action: THROW) {
						ownedBitbucketWorkspaces @required(action: THROW) {
							__typename
							# uuid, slug fileds are using in child components
							uuid @required(action: THROW)
							slug @required(action: THROW)
							name @required(action: THROW)
						}
					}
				}
			}
		`,
		oneClickConnectData,
	);
	const [{ connectState, selectedWorkspaceUuid }, { startConnection, setSelectedWorkspaceUuid }] =
		useOneClickConnectStore();
	const selectOptions = data.codeInJira.userConfiguration.ownedBitbucketWorkspaces
		.flatMap((workspace) => (workspace ? [workspace] : []))
		.map((workspace) => ({
			label: `${workspace.name} (${workspace.slug})`,
			value: workspace.uuid,
		}));
	const defaultSelectValue = selectOptions[0];
	if (selectedWorkspaceUuid === '') {
		setSelectedWorkspaceUuid(defaultSelectValue.value);
	}
	const isReadyToConnect = connectState === READY_TO_CONNECT;
	const isConnecting = connectState === IS_CONNECTING;
	const isConnected = connectState === IS_CONNECTED;

	const ARROW_LABEL = 'ARROW_LABEL';
	const CHECK_LABEL = 'CHECK';

	const ReadyToConnectIndicator = (
		<>
			<ArrowRightContainer>
				<ArrowRightIcon label={ARROW_LABEL} />
			</ArrowRightContainer>
		</>
	);

	const ConnectingIndicator = (
		<>
			<ArrowRightContainer>
				{ff('isotopes-consolidate-jfe-assets_pddkf') ? (
					<ConnectingIconContainer>
						<ConnectingWaves alt="img" fill />
					</ConnectingIconContainer>
				) : (
					<img src={connectingImg} alt="img" />
				)}
			</ArrowRightContainer>
		</>
	);

	const ConnectedIndicator = (
		<ConnectedIconContainer>
			<EditorHorizontalRuleIcon label="line" />
			<CheckCircleIcon label={CHECK_LABEL} />
			<EditorHorizontalRuleIcon label="line" />
		</ConnectedIconContainer>
	);

	let indicator;
	let actionButtons;

	switch (connectState) {
		case IS_CONNECTING:
			indicator = ConnectingIndicator;
			break;
		case IS_CONNECTED:
			indicator = ConnectedIndicator;
			break;
		case READY_TO_CONNECT:
		default:
			indicator = ReadyToConnectIndicator;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const cancelClick = (_: any, analyticsEvent: any) => {
		onExitOnClickConnectFlow();
		fireUIAnalytics(analyticsEvent, 'cancelOneClickConnectClick');
	};

	const connectClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			startConnection(baseUrl, analyticsEvent);
			fireUIAnalytics(analyticsEvent, 'connectOneClickConnectClick', {
				selectedWorkspaceUuid,
			});
		},
		[startConnection, baseUrl, selectedWorkspaceUuid],
	);

	const continueClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			onRefetch();
			fireUIAnalytics(analyticsEvent, 'continueOneClickConnectClick');
		},
		[onRefetch],
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onSelectChange = (selectedItem: any) => {
		setSelectedWorkspaceUuid(selectedItem.value);
	};

	if (connectState === IS_CONNECTED) {
		actionButtons = (
			<StyledButton appearance="primary" onClick={continueClick}>
				{formatMessage(messages.continueButtonLabel)}
			</StyledButton>
		);
	} else {
		actionButtons = (
			<>
				<StyledButton onClick={cancelClick} isDisabled={isConnecting}>
					{formatMessage(messages.cancelButtonLabel)}
				</StyledButton>
				{isReadyToConnect && (
					<StyledButton appearance="primary" onClick={connectClick}>
						{formatMessage(messages.connectButtonLabel)}
					</StyledButton>
				)}
				{isConnecting && (
					<StyledLoadingButton appearance="primary" isLoading>
						{formatMessage(messages.connectButtonLabel)}
					</StyledLoadingButton>
				)}
			</>
		);
	}
	return (
		<Wrapper>
			<LogosSection>
				<LogoWrapper>
					<JiraIcon size="xlarge" />
				</LogoWrapper>
				<ConnectIconsContainer>{indicator}</ConnectIconsContainer>
				{isConnected ? (
					<LogoWrapper>
						<BitbucketIcon size="xlarge" />
					</LogoWrapper>
				) : (
					<BitbucketLogoNotConnectWrapper>
						<BitbucketIcon size="xlarge" />
					</BitbucketLogoNotConnectWrapper>
				)}
			</LogosSection>
			<DropdownSection>
				<DropdownLabelContainer>
					<Text>
						{formatMessage(isConnected ? messages.successMessageLabel : messages.dropdownTitle)}
					</Text>
					<BaseUrlContainer>{tenantHostName}</BaseUrlContainer>
				</DropdownLabelContainer>
				<Select
					classNamePrefix="react-select"
					options={selectOptions}
					defaultValue={defaultSelectValue}
					onChange={onSelectChange}
					isSearchable={false}
				/>
			</DropdownSection>
			<ButtonsSection>{actionButtons}</ButtonsSection>
		</Wrapper>
	);
};

type ButtonProps = ComponentPropsWithRef<typeof Button>;
type LoadingButtonProps = ComponentPropsWithRef<typeof LoadingButton>;

const xlargeSize = '72px';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoWrapper = styled2.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	boxShadow: '0px 0px 1px rgba(9, 30, 66, 0.31), 0px 10px 18px rgba(9, 30, 66, 0.15)',
	height: xlargeSize,
	minWidth: xlargeSize,
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	color: 'white',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.background.brand.bold', colors.B400),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConnectedIconContainer = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon.success', colors.G300),
	width: xlargeSize,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	flex: '1 1 auto',
	padding: token('space.100', '8px'),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogosSection = styled2.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BitbucketLogoNotConnectWrapper = styled.div<any>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 1.5}px`,
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	boxShadow: '0px 0px 1px rgba(9, 30, 66, 0.31), 0px 10px 18px rgba(9, 30, 66, 0.15)',
	height: xlargeSize,
	width: xlargeSize,
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	color: 'white',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.background.neutral.pressed', colors.N50),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConnectIconsContainer = styled.div<any>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.success', colors.G300),
	width: xlargeSize,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArrowRightContainer = styled2.div({
	margin: `${token('space.100', '8px')} ${token('space.300', '24px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.neutral.pressed', colors.N50),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BaseUrlContainer = styled2.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.brand', colors.B200),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownLabelContainer = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
	textAlign: 'center',
	paddingBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownSection = styled2.div({
	display: 'flex',
	justifyContent: 'center',
	flexDirection: 'column',
	width: '400px',
	margin: `${token('space.300', '24px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConnectingIconContainer = styled2.div({
	width: '24px',
	height: '24px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	position: 'relative',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled
const StyledButton: StyledComponentClass<ButtonProps & RefAttributes<HTMLElement>, any> = styled(
	Button,
)({
	marginRight: token('space.200', '16px'),
});

const StyledLoadingButton: StyledComponentClass<
	LoadingButtonProps & RefAttributes<HTMLElement>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
> = styled(LoadingButton)({
	marginRight: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsSection = styled2.div({
	display: 'flex',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = { ButtonsSection, StyledButton, StyledLoadingButton };
